import { useEffect, useState } from "react";
import { Box, Button, Flex, Link, Text } from "@chakra-ui/react";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import FitLogo from "../fitness/FitLogo";
import { ACCENT, BG, EASE, FG, GRID, MONO, MUTED, alpha } from "../theme";
import { EMAIL, NAV, SOCIALS } from "../data/site";
import { Mono, PX } from "./ui";

const MotionBox = motion(Box);

const Burger = ({ open, onClick }) => (
  <Box
    as="button"
    aria-label={open ? "Close menu" : "Open menu"}
    aria-expanded={open}
    onClick={onClick}
    display={{ base: "flex", md: "none" }}
    w="44px"
    h="44px"
    ml="auto"
    alignItems="center"
    justifyContent="center"
    borderRadius="full"
    border={`1px solid ${GRID}`}
    position="relative"
    zIndex={2}
  >
    {[0, 1].map((i) => (
      <Box
        key={i}
        position="absolute"
        w="16px"
        h="1.5px"
        bg={FG}
        transition="transform 0.35s cubic-bezier(0.22, 1, 0.36, 1)"
        transform={
          open ? `rotate(${i ? -45 : 45}deg)` : `translateY(${i ? 3.5 : -3.5}px)`
        }
      />
    ))}
  </Box>
);

const Navbar = () => {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const isActive = (to) => pathname === to || pathname.startsWith(`${to}/`);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <>
      <Box
        as="nav"
        position="sticky"
        top={0}
        zIndex={1000}
        bg={scrolled || open ? alpha(BG, 0.78) : "transparent"}
        backdropFilter={scrolled ? "saturate(140%) blur(16px)" : "none"}
        borderBottom="1px solid"
        borderColor={scrolled ? GRID : "transparent"}
        transition="background 0.3s, border-color 0.3s"
      >
        <Flex maxW="1360px" mx="auto" align="center" px={PX} h={{ base: "64px", md: "72px" }} gap={6}>
          <Link
            as={RouterLink}
            to="/"
            display="flex"
            alignItems="center"
            gap={2.5}
            _hover={{ textDecoration: "none" }}
            role="group"
            position="relative"
            zIndex={2}
          >
            <Box transition="transform 0.5s cubic-bezier(0.22, 1, 0.36, 1)" _groupHover={{ transform: "rotate(-8deg)" }}>
              <FitLogo size={30} animate={false} />
            </Box>
            <Text color={FG} fontWeight={600} fontSize="md" letterSpacing="-0.02em">
              Pratik Chawla
            </Text>
          </Link>

          <Flex ml="auto" gap={1} display={{ base: "none", md: "flex" }} align="center">
            {NAV.map(({ label, to }) => {
              const active = isActive(to);
              return (
                <Link
                  key={to}
                  as={RouterLink}
                  to={to}
                  position="relative"
                  px={4}
                  py={2}
                  fontSize="sm"
                  fontWeight={500}
                  color={active ? FG : MUTED}
                  _hover={{ color: FG, textDecoration: "none" }}
                  transition="color 0.2s"
                >
                  {label}
                  {active && (
                    <MotionBox
                      layoutId="nav-dot"
                      position="absolute"
                      left="50%"
                      bottom="-2px"
                      w="5px"
                      h="5px"
                      ml="-2.5px"
                      borderRadius="full"
                      bg={ACCENT}
                      transition={{ type: "spring", stiffness: 380, damping: 30 }}
                    />
                  )}
                </Link>
              );
            })}
            <Button as={RouterLink} to="/contact" variant="volt" size="sm" ml={4} px={5} h="38px">
              Let’s talk
            </Button>
          </Flex>
          <Burger open={open} onClick={() => setOpen(!open)} />
        </Flex>
      </Box>

      <AnimatePresence>
        {open && (
          <MotionBox
            key="menu"
            position="fixed"
            inset={0}
            zIndex={999}
            bg={BG}
            pt="96px"
            px={PX}
            pb={8}
            display="flex"
            flexDirection="column"
            initial={{ clipPath: "inset(0 0 100% 0)" }}
            animate={{ clipPath: "inset(0 0 0% 0)" }}
            exit={{ clipPath: "inset(0 0 100% 0)" }}
            transition={{ duration: 0.55, ease: EASE }}
          >
            {[...NAV, { label: "Contact", to: "/contact" }].map(({ label, to }, i) => (
              <MotionBox
                key={to}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.15 + i * 0.06, duration: 0.5, ease: EASE }}
              >
                <Link
                  as={RouterLink}
                  to={to}
                  display="flex"
                  alignItems="baseline"
                  gap={4}
                  py={3}
                  borderBottom={`1px solid ${GRID}`}
                  _hover={{ textDecoration: "none" }}
                >
                  <Text fontFamily={MONO} fontSize="xs" color={ACCENT}>
                    0{i + 1}
                  </Text>
                  <Text
                    fontSize="44px"
                    fontWeight={600}
                    letterSpacing="-0.04em"
                    lineHeight="1.1"
                    color={isActive(to) ? ACCENT : FG}
                  >
                    {label}
                  </Text>
                </Link>
              </MotionBox>
            ))}
            <Box mt="auto">
              <Mono mb={2}>Say hello</Mono>
              <Link href={`mailto:${EMAIL}`} color={FG} fontSize="lg" fontWeight={500}>
                {EMAIL}
              </Link>
              <Flex gap={5} mt={5}>
                {SOCIALS.map(({ label, href, icon: Icon }) => (
                  <Link key={label} href={href} isExternal aria-label={label} color={MUTED} _hover={{ color: ACCENT }}>
                    <Icon size={20} />
                  </Link>
                ))}
              </Flex>
            </Box>
          </MotionBox>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
